import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import styled, { css, keyframes } from "styled-components";

import Icon from "components/atoms/Icon";
import Button, { BUTTON_STYLES } from "components/atoms/button";
import Text from "components/atoms/text/Text";

import Constants from "utils/constants";

const backdropEnter = keyframes`
    from { opacity: 0; }
    to { opacity: 1; }
`;

const backdropExit = keyframes`
    from { opacity: 1; }
    to { opacity: 0; }
`;

const MODAL_CLOSE_DURATION = 100;

let openedModalCount = 0;
let previousBodyOverflow = "";
let previousBodyPaddingRight = "";

const lockBodyScroll = () => {
    if (typeof document === "undefined") {
        return;
    }

    const body = document.body;

    if (openedModalCount === 0) {
        previousBodyOverflow = body.style.overflow;
        previousBodyPaddingRight = body.style.paddingRight;

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        body.style.overflow = "hidden";

        if (scrollbarWidth > 0) {
            body.style.paddingRight = `${scrollbarWidth}px`;
        }
    }

    openedModalCount += 1;
};

const unlockBodyScroll = () => {
    if (typeof document === "undefined" || openedModalCount === 0) {
        return;
    }

    openedModalCount -= 1;

    if (openedModalCount === 0) {
        document.body.style.overflow = previousBodyOverflow;
        document.body.style.paddingRight = previousBodyPaddingRight;
    }
};

const Components = styled.div`
    --modal-space-x: clamp(24px, 6vw, 100px);
    --modal-space-y: clamp(24px, 8vh, 100px);

    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: var(--modal-space-y) var(--modal-space-x);
    overflow-y: auto;
    overscroll-behavior: contain;
    z-index: 100;

    animation: ${({ $isClosing }) =>
        $isClosing
            ? css`
                  ${backdropExit} ${MODAL_CLOSE_DURATION}ms cubic-bezier(0.4, 0, 1, 1) forwards
              `
            : css`
                  ${backdropEnter} ${MODAL_CLOSE_DURATION}ms cubic-bezier(0.25, 1, 0.5, 1) both
              `};

    ${Constants.BLUR_OPTION}
`;

const RealBox = styled.div`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: ${(props) => (props.width ? `min(${props.width}px, calc(100vw - (var(--modal-space-x) * 2)))` : "auto")};
    max-width: calc(100vw - (var(--modal-space-x) * 2));
    height: ${(props) => (props.height ? `${props.height}px` : "auto")};
    max-height: calc(100vh - (var(--modal-space-y) * 2));
    padding: 24px;
    border-radius: 12px;
    border: solid 1px #bac9d0;
    background-color: var(--color-White);
    overflow: hidden;
`;

const InnerWrap = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
    background-color: var(--color-White);
`;

const ModalHeader = styled.div`
    box-sizing: border-box;
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    height: auto;
`;

const ModalTitleText = styled(Text).attrs({
    variant: "title-sm-500",
})``;

const CloseButton = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    border-radius: 6px;
    background-color: transparent;
    transition: all 100ms ease-in;
    cursor: pointer;

    &:hover {
        background-color: var(--color-ButtonHover4);
    }
`;

const ContentsWrap = styled.div`
    box-sizing: border-box;
    margin: 24px 0 0 0;
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding-right: 4px;
`;

const FooterWrap = styled.div`
    flex-shrink: 0;
    margin: 24px 0 0 0;
`;

const FooterButtons = styled.div`
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    height: 32px;
`;

function ModalFooter({ footer }) {
    if (React.isValidElement(footer)) {
        return footer;
    }

    return (
        <FooterButtons>
            {footer.onlyLeftBtn ? (
                <Button
                    {...BUTTON_STYLES.modalSecondary}
                    onClick={footer.leftOnClick}
                    buttonText={footer.leftBtnText}
                />
            ) : (
                <>
                    <Button
                        {...BUTTON_STYLES.modalSecondary}
                        onClick={footer.leftOnClick}
                        buttonText={footer.leftBtnText}
                    />
                    <Button
                        {...BUTTON_STYLES.modalPrimary}
                        onClick={footer.rightOnClick}
                        margin={"0 0 0 4px"}
                        buttonText={footer.rightBtnText}
                        disabled={footer.disabled}
                    />
                </>
            )}
        </FooterButtons>
    );
}

function AbstractModal({ modalTitle, width, height, children, exitModal, id, footer }) {
    const [isClosing, setIsClosing] = useState(false);

    const handleClose = () => {
        if (isClosing) {
            return;
        }

        setIsClosing(true);
        window.setTimeout(exitModal, MODAL_CLOSE_DURATION);
    };

    useEffect(() => {
        lockBodyScroll();

        return () => {
            unlockBodyScroll();
        };
    }, []);

    if (typeof document === "undefined") {
        return null;
    }

    return createPortal(
        <Components className="modalContainer" id={id} $isClosing={isClosing}>
            <RealBox width={width} height={height}>
                <InnerWrap>
                    <ModalHeader>
                        <ModalTitleText>{modalTitle}</ModalTitleText>
                        <CloseButton onClick={handleClose}>
                            <Icon name={"x"} color={"var(--color-SubBlack)"} />
                        </CloseButton>
                    </ModalHeader>
                    <ContentsWrap>{children}</ContentsWrap>
                    {footer && (
                        <FooterWrap>
                            <ModalFooter footer={footer} />
                        </FooterWrap>
                    )}
                </InnerWrap>
            </RealBox>
        </Components>,
        document.body,
    );
}

export default AbstractModal;
